export type ModelProvider = 'openai' | 'google' | 'xai' | 'anthropic' | 'auto' | 'openrouter';

export interface ModelInfo {
  id: string;
  name: string;
  provider: ModelProvider;
  recommended?: boolean;
  requireKey?: boolean;
}

export const MODELS = [
  {
    id: 'auto',
    name: 'Auto',
    recommended: true,
    provider: 'auto',
    requireKey: false,
  },
  {
    id: 'claude-4.6-sonnet',
    name: 'Claude Sonnet 4.6',
    provider: 'anthropic',
    recommended: true,
    requireKey: false,
  },
  {
    id: 'claude-4.5-sonnet',
    name: 'Claude 4.5 Sonnet',
    provider: 'anthropic',
    recommended: false,
    requireKey: false,
  },
  {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    recommended: false,
    provider: 'google',
    requireKey: false,
  },
  {
    id: 'gpt-4.1',
    name: 'GPT-4.1',
    provider: 'openai',
    recommended: false,
    requireKey: false,
  },
  {
    id: 'gpt-5',
    name: 'GPT-5',
    provider: 'openai',
    recommended: false,
    requireKey: false,
  },
  {
    id: 'grok-3-mini',
    name: 'Grok 3 Mini',
    provider: 'xai',
    recommended: false,
    requireKey: false,
  },
  {
    id: 'openrouter-deepseek-coder',
    name: 'DeepSeek Coder (OpenRouter)',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'claude-3-5-haiku',
    name: 'Claude 3.5 Haiku',
    provider: 'anthropic',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'gpt-4.1-mini',
    name: 'GPT-4.1 Mini',
    provider: 'openai',
    recommended: false,
    requireKey: true,
  },
  // New OpenRouter Models
  {
    id: 'qwen/qwen3.6-plus',
    name: 'Qwen 3.6 Plus',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'moonshotai/kimi-k2.6',
    name: 'Kimi K2.6',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'anthropic/claude-opus-4.7',
    name: 'Claude Opus 4.7',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'anthropic/claude-sonnet-4.6',
    name: 'Claude Sonnet 4.6 (OpenRouter)',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'google/gemini-3-flash-preview',
    name: 'Gemini 3 Flash Preview',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'minimax/minimax-m2.5',
    name: 'Minimax M2.5',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'minimax/minimax-m2.7',
    name: 'Minimax M2.7',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'openai/gpt-5.4',
    name: 'GPT-5.4',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'google/gemini-3.1-pro-preview',
    name: 'Gemini 3.1 Pro Preview',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'z-ai/glm-5',
    name: 'GLM-5',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'google/gemini-3.1-flash-lite-preview',
    name: 'Gemini 3.1 Flash Lite Preview',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'openai/gpt-5.3-codex',
    name: 'GPT-5.3 Codex',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'openai/gpt-oss-20b:nitro',
    name: 'GPT OSS 20B Nitro',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
  {
    id: 'xiaomi/mimo-v2-pro',
    name: 'Mimo v2 Pro',
    provider: 'openrouter',
    recommended: false,
    requireKey: true,
  },
] as const;

export type ModelSelection = (typeof MODELS)[number]['id'];

export function getModelInfo(modelId: string): ModelInfo | undefined {
  return MODELS.find((m) => m.id === modelId);
}

export function getModelProvider(modelId: string): ModelProvider {
  const model = getModelInfo(modelId);
  return model?.provider || 'auto';
}
